import React, { useState } from 'react';
import { isDevelopment } from '../config/constants';
import styles from '../styles/panelList.module.css';
import { t } from '../i18n/translate';

interface PanelItem {
  title: string;
  subtext: string;
  image_url: string;
  link_type: 'external' | 'document' | 'route';
  link_value: string;
}

interface PanelListWithImagesProps {
  title: string;
  subtext: string;
  items: PanelItem[];
  isExpanded?: boolean;
  iconUrl?: string;
  accentColor?: string;
  showItemCount?: boolean;
}

const PanelListWithImages: React.FC<PanelListWithImagesProps> = ({
  title,
  subtext,
  items,
  isExpanded = false,
  iconUrl,
  accentColor = '#4CAF50',
  showItemCount = true
}) => {
  const [expanded, setExpanded] = useState(isExpanded);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleItemClick = (item: PanelItem) => {
    // Validate link_value exists and is not null/undefined/empty
    if (item.link_value == null || item.link_value === '' || item.link_value === 'null' || item.link_value === 'undefined') {
      if (isDevelopment()) console.warn('Cannot open link: link_value is missing or invalid', {
        item,
        link_value: item.link_value,
        link_value_type: typeof item.link_value
      });
      return;
    }

    if (item.link_type === 'external' || item.link_type === 'document') {
      window.open(item.link_value, '_blank');
    } else if (item.link_type === 'route') {
      window.location.href = item.link_value;
    }
  };

  const headerStyle: React.CSSProperties = accentColor ? {
    borderLeft: `4px solid ${accentColor}`
  } : {};

  return (
    <div className={styles.panel}>
      <div 
        className={styles.panelHeader} 
        onClick={toggleExpanded}
        style={headerStyle}
      >
        <div className={styles.panelTitleSection}>
          <div className={styles.panelTitleRow}>
            {iconUrl && (
              <img 
                src={iconUrl} 
                alt="" 
                className={styles.panelIcon}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <div className={styles.panelTitleContent}>
              <div className={styles.panelTitleRowTop}>
                <h3 className={styles.panelTitle}>{title}</h3>
                {showItemCount && items.length > 0 && (
                  <span className={styles.itemCountBadge}>{items.length}</span>
                )}
              </div>
              {subtext && <p className={styles.panelSubtext}>{subtext}</p>}
            </div>
          </div>
        </div>
        <button 
          className={`${styles.expandButton} ${expanded ? styles.expanded : ''}`}
          aria-label={expanded ? t('ui.COLLAPSE') : t('ui.EXPAND')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>
      
      {expanded && (
        <div className={styles.panelContent}>
          <div className={styles.imageListGrid}>
            {items.map((item, index) => {
              // Check if link_value is valid (not null, undefined, empty string, or string 'null'/'undefined')
              const hasValidLink = item.link_value != null && 
                                   item.link_value !== '' && 
                                   item.link_value !== 'null' && 
                                   item.link_value !== 'undefined' &&
                                   typeof item.link_value === 'string';
              
              if (isDevelopment() && item.link_type === 'document' && !hasValidLink) {
                console.log('Document item without valid link:', {
                  index,
                  title: item.title,
                  link_type: item.link_type,
                  link_value: item.link_value,
                  link_value_type: typeof item.link_value
                });
              }
              
              return (
              <div 
                key={index} 
                className={`${styles.imageListItem} ${!hasValidLink ? styles.disabledItem : ''}`}
                onClick={() => hasValidLink && handleItemClick(item)}
                style={{ cursor: hasValidLink ? 'pointer' : 'default' }}
              >
                <div className={styles.imageContainer}>
                  {item.image_url ? (
                    <img 
                      src={item.image_url} 
                      alt={item.title}
                      className={styles.itemImage}
                      loading="lazy"
                    />
                  ) : (
                    <div className={styles.placeholderImage}>
                      No Image
                    </div>
                  )}
                </div>
                <div className={styles.itemContent}>
                  <h4 className={styles.itemTitle}>{item.title}</h4>
                  <p className={styles.itemSubtext}>{item.subtext}</p>
                  <div className={styles.itemLink}>
                    {hasValidLink ? (
                      <>
                        {item.link_type === 'document' ? t('panels.open') : t('panels.learn_more')}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </>
                    ) : (
                      <span style={{ color: '#999', fontStyle: 'italic' }}>{t('panels.no_link')}</span>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelListWithImages;


